import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, absFile } from "@/lib/api";
import { PhotoFrame } from "@/components/Motifs";

const TILTS = [-2, 1.5, -1, 2, -1.5, 1];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");
  const [idx, setIdx] = useState(null);
  useEffect(() => {
    api.get("/public/gallery").then((r) => setItems(r.data || []));
    api.get("/public/events").then((r) => setEvents(r.data || []));
  }, []);
  const shown = filter ? items.filter((it) => it.event_slug === filter) : items;
  useEffect(() => {
    if (idx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % shown.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + shown.length) % shown.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, shown.length]);

  return (
    <div data-testid="gallery-page">
      <section className="bg-[#F7B733] border-b-2 border-[#252122]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24">
          <div className="kicker text-[#A62D58] rise">Gallery</div>
          <h1 className="font-display text-6xl md:text-[7.5rem] leading-[0.85] tracking-[-0.04em] mt-4 rise d1">Camera roll,<br /><span className="italic">but make it official.</span></h1>
          {items.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-10">
              <Chip active={!filter} onClick={() => setFilter("")} testid="gallery-filter-all">Everything</Chip>
              {events.map((e) => <Chip key={e.slug} active={filter === e.slug} onClick={() => setFilter(e.slug)} testid={`gallery-filter-${e.slug}`}>{e.name}</Chip>)}
            </div>
          )}
        </div>
      </section>
      <section className="bg-[#FFF9F0] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          {shown.length === 0 ? (
            <div className="grid md:grid-cols-12 gap-10 items-center" data-testid="gallery-empty">
              <div className="md:col-span-5 relative h-[360px]">
                <PhotoFrame initial="soon" tone="#F7C9D0" rotate={-6} className="absolute left-0 top-0 w-[60%]" caption="developing..." />
                <PhotoFrame initial="11:11" tone="#A9C79D" rotate={5} className="absolute right-0 bottom-0 w-[55%]" ratio="aspect-square" caption="still developing..." />
              </div>
              <div className="md:col-span-7">
                <h2 className="font-display text-4xl md:text-6xl leading-tight">The album is still <span className="italic text-[#A62D58]">developing.</span></h2>
                <p className="font-hand text-2xl mt-4">Photos land here once the celebrations begin. Check back after 04.12.2026.</p>
              </div>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 gap-5 md:gap-7">
              {shown.map((it, i) => (
                <button key={it.id} onClick={() => setIdx(i)} data-testid={`gallery-item-${it.id}`} className="polaroid mb-5 md:mb-7 w-full break-inside-avoid bg-white p-2.5 pb-10 relative shadow-[0_14px_30px_-12px_rgba(37,33,34,0.35)] block" style={{ "--r": `${TILTS[i % TILTS.length]}deg` }}>
                  <img src={absFile(it.url)} alt={it.caption || "Wedding moment"} loading="lazy" className="w-full h-auto block" />
                  <span className="font-hand absolute bottom-2 inset-x-2 text-center text-[#252122]/75 truncate">{it.caption}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
      {idx !== null && shown[idx] && (
        <div className="fixed inset-0 bg-[#252122]/95 z-50 flex items-center justify-center p-4" data-testid="lightbox" onClick={() => setIdx(null)}>
          <button data-testid="lightbox-close" className="absolute top-5 right-5 text-[#FFF9F0]" aria-label="Close" onClick={() => setIdx(null)}><X size={28} /></button>
          <button data-testid="lightbox-prev" className="absolute left-3 md:left-8 text-[#FFF9F0] p-2" aria-label="Previous" onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + shown.length) % shown.length); }}><ChevronLeft size={36} /></button>
          <figure className="max-w-5xl page-enter" onClick={(e) => e.stopPropagation()}>
            <img src={absFile(shown[idx].url)} alt={shown[idx].caption || ""} className="max-h-[80vh] max-w-full mx-auto" />
            {shown[idx].caption && <figcaption className="font-hand text-2xl text-[#F7C9D0] text-center mt-4">{shown[idx].caption}</figcaption>}
          </figure>
          <button data-testid="lightbox-next" className="absolute right-3 md:right-8 text-[#FFF9F0] p-2" aria-label="Next" onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % shown.length); }}><ChevronRight size={36} /></button>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`btn-pop rounded-full px-5 py-2.5 kicker ${active ? "bg-[#252122] text-[#FFF9F0]" : "bg-[#FFF9F0]"}`}>{children}</button>;
}
